import type { AdditionalParams, ApiCallResult } from './apiClient';
import { apiPost, apiDelete, apiGet } from './http';
import { getPath } from './getPath';
import type { LoadedItem, ItemUpdateRequestDto } from '../dto/searchResultHit';
import type Relations from '../dto/relationModels';
import type { Relation } from '../dto/relationModels';
import type { TransferToArchiveBucketRequestDto, TransferToArchiveBucketResponse } from '../dto/archivingDtos';
import type { BackendClient } from './backendClient';
import { assumeResultArray, assumeResultObject } from '../client/assumeResult';
import type { MdfEntity } from '../dto/mdfModels';




export const ofArchiveTransfer = (sl: { archiveTransfer: string }, itemId: string) => getPath(sl.archiveTransfer, { itemId });

export const ofItem = (sl: { item: string }, itemId: string) => getPath(sl.item, { itemId });

export const ofMdf = (sl: { mdf: string }, mdfId: string) => getPath(sl.mdf, { mdfId });

export const ofRelations = (sl: { relations: string }, itemId: string) => getPath(sl.relations, { itemId });


type UpdateItemOutput = Partial<LoadedItem> & Pick<LoadedItem, 'id'>;
export async function apiUpdateItem(client: BackendClient, itemId: string, payload: ItemUpdateRequestDto, options?: AdditionalParams): Promise<Partial<LoadedItem> & Pick<LoadedItem, 'id'>> {
  const response = await apiPost(client.getApiClient(), ofItem(client.getServiceLinks(), itemId), payload, options);
  return assumeResultObject<UpdateItemOutput>(response, 'id');
}

export async function apiGetItem(client: BackendClient, itemId: string, options?: AdditionalParams): Promise<LoadedItem> {
  const response = await apiGet(client.getApiClient(), ofItem(client.getServiceLinks(), itemId), options);
  return assumeResultObject<LoadedItem>(response, 'id');
}

export async function apiGetMdf(client: BackendClient, mdfId: string, options?: AdditionalParams): Promise<MdfEntity> {
  const response = await apiGet(client.getApiClient(), ofMdf(client.getServiceLinks(), encodeURIComponent(mdfId)), options);
  return assumeResultObject<MdfEntity>(response, 'id');
}

export async function apiGetRelations(client: BackendClient, itemId: string, options?: AdditionalParams): Promise<Relations> {
  const response = await apiGet(client.getApiClient(), ofRelations(client.getServiceLinks(), itemId), options);
  return assumeResultArray<Relation>(response);
}


/**
 * Deletes an item. If immediate is true, the item is immediately deleted,
 * otherwise it is marked for deletion and will be visible in the recycle
 * bin until purge or restoration.
 *
 * This function does not throw an exception if the item does not exist,
 * possibly due to already having been deleted by someone else.
 */
export function apiDeleteItem(c: BackendClient, itemId: string, immediate: boolean, executeSameActionOnCleanCopy?: boolean, promoteNewVersion?: boolean): Promise<ApiCallResult> {
  return apiDelete(
    c.getApiClient(),
    ofItem(c.getServiceLinks(), itemId),
    executeSameActionOnCleanCopy ? { executeSameActionOnArchiveCopy: true } : {},
    { queryParams: { 'immediate': immediate, 'promoteNewVersion': promoteNewVersion }},
  );
}

export const requestTransferToArchiveStorage = async (client: BackendClient, itemId: string, transferRequest: TransferToArchiveBucketRequestDto): Promise<TransferToArchiveBucketResponse> => {
  const result = await apiPost(client.getApiClient(), ofArchiveTransfer(client.getServiceLinks(), itemId), transferRequest);
  return assumeResultObject<TransferToArchiveBucketResponse>(result, 'uploadLocationId');
};
